import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Guideline, GuidelineCategory, GuidelineStatus, GuidelineRecord } from '@/types/community/guideline';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Constants
const GUIDELINES_COLLECTION = 'guidelines';

// Types
export interface GuidelinesQueryParams {
  searchTerm?: string;
  category?: GuidelineCategory;
  status?: GuidelineStatus;
  blockId?: string;
  apartmentUnitId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

type GuidelineWithDates = Partial<Guideline>;

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; category?: string; status?: string; block?: { id?: string }; apartmentUnit?: { id?: string } }>(
  data: T[],
  searchTerm?: string,
  category?: string,
  status?: string,
  blockId?: string,
  apartmentUnitId?: string
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (category && item.category !== category) return false;
    if (status && item.status !== status) return false;
    if (blockId && item.block?.id !== blockId) return false;
    if (apartmentUnitId && item.apartmentUnit?.id !== apartmentUnitId) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// Guidelines API
export async function getGuidelines({
  searchTerm,
  category,
  status,
  blockId,
  apartmentUnitId,
  pageSize,
  page = 1,
  firestoreUser
}: GuidelinesQueryParams): Promise<{ guidelines: Guideline[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockGuidelines = await loadMockData<Guideline[]>('guidelines', firestoreUser.language);
    const filteredData = filterMockData(mockGuidelines, searchTerm, category, status, blockId, apartmentUnitId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      guidelines: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const guidelinesRef = collection(db, GUIDELINES_COLLECTION);
    const q = query(
      guidelinesRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { guidelines: [], total: 0 };
    }

    const guidelineData = snapshot.docs[0].data() as GuidelineWithDates;
    const mappedGuideline: GuidelineWithDates = {
      ...guidelineData,
      id: snapshot.docs[0].id,
      effectiveDate: guidelineData.effectiveDate ? formatFirestoreTimestamp(guidelineData.effectiveDate) ?? undefined : undefined,
      expiryDate: guidelineData.expiryDate ? formatFirestoreTimestamp(guidelineData.expiryDate) ?? undefined : undefined
    };
    
    // Only return if all required fields are present
    if (
      typeof mappedGuideline.status === 'string' &&
      typeof mappedGuideline.version === 'string' &&
      mappedGuideline.effectiveDate instanceof Date &&
      mappedGuideline.workspace && typeof mappedGuideline.workspace.id === 'string' &&
      Array.isArray(mappedGuideline.records)
    ) {
      return { guidelines: [mappedGuideline as Guideline], total: 1 };
    }
    
    return { guidelines: [], total: 0 };
  } catch (error) {
    console.error('Error fetching guidelines:', error);
    throw new Error('Failed to fetch guidelines');
  }
}

export async function getGuidelineById(id: string, firestoreUser: User): Promise<Guideline | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockGuidelines = await loadMockData<Guideline[]>('guidelines', firestoreUser.language);
    const guideline = mockGuidelines.find(g => g.id === id) || null;
    return guideline;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const guidelinesRef = collection(db, GUIDELINES_COLLECTION);
    const q = query(
      guidelinesRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const guidelineData = snapshot.docs[0].data() as GuidelineWithDates;
    const mappedGuideline: GuidelineWithDates = {
      ...guidelineData,
      id: snapshot.docs[0].id,
      effectiveDate: guidelineData.effectiveDate ? formatFirestoreTimestamp(guidelineData.effectiveDate) ?? undefined : undefined,
      expiryDate: guidelineData.expiryDate ? formatFirestoreTimestamp(guidelineData.expiryDate) ?? undefined : undefined
    };
    
    if (
      typeof mappedGuideline.status === 'string' &&
      typeof mappedGuideline.version === 'string' &&
      mappedGuideline.effectiveDate instanceof Date &&
      mappedGuideline.workspace && typeof mappedGuideline.workspace.id === 'string' &&
      Array.isArray(mappedGuideline.records)
    ) {
      return mappedGuideline as Guideline;
    }
    return null;
  } catch (error) {
    console.error('Error fetching guideline:', error);
    throw new Error('Failed to fetch guideline');
  }
}

export async function addGuideline(
  guideline: Omit<Guideline, 'id'>,
  firestoreUser: User
): Promise<string> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Check if guideline already exists for this workspace
    const existingGuidelines = await getGuidelines({ firestoreUser });
    if (existingGuidelines.guidelines.length > 0) {
      throw new Error('Guideline already exists for this workspace. Use update instead.');
    }

    // Validate required fields
    if (!guideline.status) {
      throw new Error('Guideline status is required');
    }
    if (!guideline.version?.trim()) {
      throw new Error('Guideline version is required');
    }
    if (!guideline.effectiveDate) {
      throw new Error('Guideline effective date is required');
    }
    if (!Array.isArray(guideline.records) || guideline.records.length === 0) {
      throw new Error('At least one guideline record is required');
    }

    // Validate records
    for (const record of guideline.records) {
      if (!record.content?.trim()) {
        throw new Error('All guideline records must have content');
      }
      if (!record.category) {
        throw new Error('All guideline records must have a category');
      }
    }

    const guidelineData = addWorkspaceAndActivity(
      {
        ...guideline,
        effectiveDate: guideline.effectiveDate,
        expiryDate: guideline.expiryDate,
        author: guideline.author || undefined,
        records: guideline.records,
        activityRecords: guideline.activityRecords || undefined
      },
      firestoreUser,
      'created'
    );

    const guidelinesRef = collection(db, GUIDELINES_COLLECTION);
    const newGuidelineRef = doc(guidelinesRef);
    
    const finalData = removeUndefined({
      ...guidelineData,
      id: newGuidelineRef.id
    });

    await setDoc(newGuidelineRef, finalData);

    return newGuidelineRef.id;
  } catch (error: any) {
    console.error('Error adding guideline:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to create guidelines');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided: Please check all required fields');
    } else if (error.message) {
      throw new Error(`Failed to add guideline: ${error.message}`);
    } else {
      throw new Error('Failed to add guideline: Unknown error occurred');
    }
  }
}

export async function updateGuideline(
  id: string,
  data: Partial<Omit<Guideline, 'id'>>,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    if (!id) {
      throw new Error('Guideline ID is required for update');
    }

    // Get existing guideline to create activity record
    const existingGuideline = await getGuidelineById(id, firestoreUser);
    if (!existingGuideline) {
      throw new Error('Guideline not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    const updateData = removeUndefined({
      ...data,
      effectiveDate: data.effectiveDate,
      expiryDate: data.expiryDate,
      author: data.author || undefined,
      records: data.records || undefined,
      activityRecords: [activityRecord]
    });

    const guidelinesRef = collection(db, GUIDELINES_COLLECTION);
    const q = query(
      guidelinesRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Guideline not found');
    }

    await updateDoc(snapshot.docs[0].ref, updateData);
  } catch (error: any) {
    console.error('Error updating guideline:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to update guidelines');
    } else if (error.code === 'not-found') {
      throw new Error('Guideline not found');
    } else if (error.message) {
      throw new Error(`Failed to update guideline: ${error.message}`);
    } else {
      throw new Error('Failed to update guideline: Unknown error occurred');
    }
  }
}

export async function deleteGuideline(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const guidelinesRef = collection(db, GUIDELINES_COLLECTION);
    const q = query(
      guidelinesRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Guideline not found');
    }

    await deleteDoc(snapshot.docs[0].ref);
  } catch (error) {
    console.error('Error deleting guideline:', error);
    throw new Error('Failed to delete guideline');
  }
}

export async function getGuidelinesByCategory(category: GuidelineCategory, firestoreUser: User): Promise<GuidelineRecord[]> {
  const { guidelines } = await getGuidelines({ firestoreUser });
  if (guidelines.length === 0) return [];
  
  const allRecords: GuidelineRecord[] = [];
  guidelines.forEach(guideline => {
    if (guideline.records) {
      allRecords.push(...guideline.records.filter(record => record.category === category));
    }
  });
  
  return allRecords;
}

export async function getGuidelinesByStatus(status: GuidelineStatus, firestoreUser: User): Promise<Guideline | null> {
  const { guidelines } = await getGuidelines({ firestoreUser });
  const guideline = guidelines.find(g => g.status === status);
  return guideline || null;
} 